<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>互联 IP 分配</strong>
        <small class="text-muted ml-2">按 /30（v4）/ /126（v6）从网段池分配给两端（供 Tunnel/BGP 互联使用，仅记录占用）</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openPoolAddModal">添加网段池</CButton>
            <CButton color="secondary" size="sm" :disabled="loading" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <div class="mb-3">
          <span class="mr-2 text-muted">地域筛选：</span>
          <CButton size="sm" class="mr-1" :color="regionFilter === '' ? 'primary' : 'light'" @click="regionFilter = ''">
            全部 ({{ pools.length }})
          </CButton>
          <CButton
            v-for="g in regions"
            :key="g.id"
            size="sm"
            class="mr-1"
            :color="regionFilter === g.id ? 'primary' : 'light'"
            @click="regionFilter = g.id"
          >
            {{ g.id === 0 ? '跨地域' : g.name }} ({{ poolsOfRegion(g.id).length }})
          </CButton>
        </div>

        <CAlert v-if="!loading && filteredPools.length === 0" color="info" fade>
          暂无网段池。添加一个网段（v4 如 33.100.100.0/24、v6 如 2001:db8:1::/64），系统会按 /30 / /126 切分并自动分配空闲网段。
        </CAlert>

        <div v-for="p in filteredPools" :key="p.id" class="mb-3">
          <div class="pool-header">
            <strong>{{ p.network }}</strong>
            <CBadge v-if="p.region_id !== null && p.region_id !== undefined" :color="p.region_id === 0 ? 'warning' : 'dark'" class="ml-2">
              {{ p.region_id === 0 ? '全局·跨地域互联' : regionName(p.region_id) }}
            </CBadge>
            <CBadge v-else color="secondary" class="ml-2">未设置地域</CBadge>
            <CBadge :color="isV6Pool(p) ? 'dark' : 'info'" class="ml-2">
              {{ isV6Pool(p) ? 'IPv6 池' : 'IPv4 池' }} · 已用 {{ p.used }} / {{ p.total < 0 ? '∞' : p.total }} 个 {{ isV6Pool(p) ? '/126' : '/30' }}
            </CBadge>
            <small v-if="p.remark" class="text-muted ml-2">{{ p.remark }}</small>
            <span class="ml-auto">
              <CButtonGroup size="sm">
                <CButton color="success" size="sm" @click="openAllocModal(p)">分配</CButton>
                <CButton color="info" size="sm" @click="openPoolEditModal(p)">编辑</CButton>
                <CButton color="danger" size="sm" :disabled="p.used > 0" @click="deletePool(p)">删除池</CButton>
              </CButtonGroup>
            </span>
          </div>
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th width="160">网段</th>
                <th>A（类型/名称）</th>
                <th>IP A</th>
                <th>A 网卡</th>
                <th>B（类型/名称）</th>
                <th>IP B</th>
                <th>B 网卡</th>
                <th>备注</th>
                <th width="130">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in p.allocations" :key="a.id">
                <td><code>{{ a.cidr }}</code></td>
                <td>
                  <CBadge :color="a.owner_a_type === 'cmdb' ? 'info' : 'secondary'" class="mr-1">{{ a.owner_a_type }}</CBadge>
                  {{ a.owner_a_name || '-' }}
                </td>
                <td><code>{{ a.a_ip }}</code></td>
                <td>
                  <template v-if="a.owner_a_type === 'router'">
                    <CBadge v-if="a.a_interface" color="success" class="mr-1" title="已下发">{{ a.a_interface }}</CBadge>
                    <CBadge v-else color="secondary">未分配</CBadge>
                  </template>
                  <template v-else>-</template>
                </td>
                <td>
                  <CBadge :color="a.owner_b_type === 'cmdb' ? 'info' : 'secondary'" class="mr-1">{{ a.owner_b_type }}</CBadge>
                  {{ a.owner_b_name || '-' }}
                </td>
                <td><code>{{ a.b_ip }}</code></td>
                <td>
                  <template v-if="a.owner_b_type === 'router'">
                    <CBadge v-if="a.b_interface" color="success" class="mr-1" title="已下发">{{ a.b_interface }}</CBadge>
                    <CBadge v-else color="secondary">未分配</CBadge>
                  </template>
                  <template v-else>-</template>
                </td>
                <td><small>{{ a.remark || '' }}</small></td>
                <td>
                  <CButtonGroup size="sm">
                    <CButton color="info" size="sm" @click="openPushModal(a)">下发</CButton>
                    <CButton color="danger" size="sm" @click="releaseAllocation(a, p)">释放</CButton>
                  </CButtonGroup>
                </td>
              </tr>
              <tr v-if="p.allocations.length === 0">
                <td colspan="7" class="text-center text-muted">暂无分配</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>

    <!-- 添加池 Modal -->
    <CModal :show.sync="poolModal.show" title="添加网段池" color="primary">
      <CForm>
        <CInput label="网段（CIDR, 支持 v4/v6）" v-model="poolModal.network" placeholder="v4 如 33.100.100.0/24 / v6 如 2001:db8:1::/64" />
        <CSelect label="归属地域" v-model="poolModal.region_id" :options="regionSelectOptions" />
        <CInput label="备注" v-model="poolModal.remark" placeholder="如 sz-fs-backbone-interconnect" />
        <CAlert color="info" fade>系统将按 /30（v4）/ /126（v6）切分该网段，每次分配取一个空闲子网给两端（各拿 .1/.2 或 ::1/::2）。「全局」地域的网段用于跨地域互联。</CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="poolModal.show = false">取消</CButton>
          <CButton color="success" :disabled="poolModal.loading" @click="submitPool">{{ poolModal.loading ? '创建中...' : '创建' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 编辑池 Modal -->
    <CModal :show.sync="poolEditModal.show" :title="'编辑网段池 - ' + (poolEditModal.pool ? poolEditModal.pool.network : '')" color="info">
      <CForm>
        <CInput label="网段" :value="poolEditModal.pool ? poolEditModal.pool.network : ''" disabled />
        <CSelect label="归属地域" v-model="poolEditModal.region_id" :options="regionSelectOptions" />
        <CInput label="备注" v-model="poolEditModal.remark" />
        <CAlert color="info" fade>跨地域互联（不同地域路由器之间）的网段请归属「全局」。</CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="poolEditModal.show = false">取消</CButton>
          <CButton color="success" :disabled="poolEditModal.loading" @click="submitPoolEdit">{{ poolEditModal.loading ? '保存中...' : '保存' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 分配 Modal -->
    <CModal :show.sync="allocModal.show" :title="'分配互联 IP - ' + (allocModal.pool ? allocModal.pool.network : '')" color="success">
      <CForm>
        <div class="form-group">
          <label class="form-label">地域（选择后过滤 Router 列表）</label>
          <SearchSelect :options="regionSelectOptions" v-model="allocModal.region_id" placeholder="（全部地域）" />
        </div>
        <CRow class="mt-2">
          <CCol sm="6">
            <CSelect label="A 类型" v-model="allocModal.owner_a_type" :options="[
              { value: 'router', label: 'Router' },
              { value: 'cmdb', label: 'CMDB 设备' }
            ]" />
          </CCol>
          <CCol sm="6">
            <div class="form-group">
              <label class="form-label">{{ allocModal.owner_a_type === 'cmdb' ? 'A - CMDB 设备（拿 .1）' : 'A - Router（拿 .1）' }}</label>
              <SearchSelect
                :options="allocModal.owner_a_type === 'cmdb' ? cmdbOptions : regionRouterOptions"
                v-model="allocModal.owner_a_id"
                placeholder="输入名称筛选"
              />
            </div>
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="6">
            <CSelect label="B 类型" v-model="allocModal.owner_b_type" :options="[
              { value: 'router', label: 'Router' },
              { value: 'cmdb', label: 'CMDB 设备' }
            ]" />
          </CCol>
          <CCol sm="6">
            <div class="form-group">
              <label class="form-label">{{ allocModal.owner_b_type === 'cmdb' ? 'B - CMDB 设备（拿 .2）' : 'B - Router（拿 .2）' }}</label>
              <SearchSelect
                :options="allocModal.owner_b_type === 'cmdb' ? cmdbOptions : regionRouterOptions"
                v-model="allocModal.owner_b_id"
                placeholder="输入名称筛选"
              />
            </div>
          </CCol>
        </CRow>
        <CInput label="指定子网（留空 = 自动取空闲）" v-model="allocModal.cidr" :placeholder="allocModal.pool && isV6Pool(allocModal.pool) ? '如 2001:db8:1::4/126' : '如 33.100.100.148/30'" />
        <CInput label="备注" v-model="allocModal.remark" placeholder="如 tunnel-sz-fs-core" />
        <div v-if="allocModal.freeList.length" class="mb-2">
          <small class="text-muted">空闲子网预览：</small>
          <CBadge v-for="f in allocModal.freeList" :key="f" color="light" class="mr-1 mb-1">{{ f }}</CBadge>
        </div>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="allocModal.show = false">取消</CButton>
          <CButton color="success" :disabled="allocModal.loading" @click="submitAlloc">{{ allocModal.loading ? '分配中...' : '分配' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 下发到网卡 Modal -->
    <CModal :show.sync="pushModal.show" :title="'下发互联 IP - ' + (pushModal.item ? pushModal.item.cidr : '')" color="info">
      <CForm v-if="pushModal.item">
        <CAlert color="info" fade>
          把互联 IP 直接下发到两端路由器的指定网卡（不经过 Tunnel）。清空/不选的一端不下发。
        </CAlert>
        <div class="form-group" v-if="pushModal.item.owner_a_type === 'router'">
          <label class="form-label">A 端网卡（{{ pushModal.item.owner_a_name }} / {{ pushModal.item.a_ip }}）</label>
          <SearchSelect :options="pushModal.aInterfaceOptions" v-model="pushModal.a_interface" placeholder="输入网卡名筛选（不选=不下发）" />
        </div>
        <CAlert v-else color="secondary" fade>A 端为 CMDB 设备，无法下发</CAlert>
        <div class="form-group" v-if="pushModal.item.owner_b_type === 'router'">
          <label class="form-label">B 端网卡（{{ pushModal.item.owner_b_name }} / {{ pushModal.item.b_ip }}）</label>
          <SearchSelect :options="pushModal.bInterfaceOptions" v-model="pushModal.b_interface" placeholder="输入网卡名筛选（不选=不下发）" />
        </div>
        <CAlert v-else color="secondary" fade>B 端为 CMDB 设备，无法下发</CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="pushModal.show = false">取消</CButton>
          <CButton color="success" :disabled="pushModal.loading" @click="submitPush">{{ pushModal.loading ? '下发中...' : '下发' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
import SearchSelect from '@/components/SearchSelect'

export default {
  name: 'LinkIpList',
  components: { SearchSelect },
  data() {
    return {
      loading: false,
      pools: [],
      regions: [],
      regionFilter: '',
      allRouters: [],
      cmdbDevices: [],
      poolModal: {
        show: false,
        loading: false,
        network: '',
        region_id: 0,
        remark: ''
      },
      poolEditModal: {
        show: false,
        loading: false,
        pool: null,
        region_id: 0,
        remark: ''
      },
      pushModal: {
        show: false,
        loading: false,
        item: null,
        a_interface: '',
        b_interface: '',
        aInterfaceOptions: [],
        bInterfaceOptions: []
      },
      allocModal: {
        show: false,
        loading: false,
        pool: null,
        region_id: '',
        owner_a_type: 'router',
        owner_a_id: '',
        owner_b_type: 'router',
        owner_b_id: '',
        cidr: '',
        remark: '',
        freeList: []
      },
      confirmModal: {
        show: false, title: '确认', message: '', callback: () => {}
      }
    }
  },
  computed: {
    filteredPools() {
      return this.poolsOfRegion(this.regionFilter)
    },
    regionRouterOptions() {
      // 全局(0) 或未选 = 不限制（跨地域互联两端可分属不同地域）
      const rid = this.allocModal.region_id
      const list = (rid === '' || rid === 0)
        ? this.allRouters
        : this.allRouters.filter(r => r.region_id === rid)
      return list.map(r => ({ value: r.id, label: r.name }))
    },
    regionSelectOptions() {
      return this.regions.map(g => ({
        value: g.id,
        label: g.id === 0 ? '全局（跨地域互联）' : `${g.name} (${g.region_id})`
      }))
    },
    routerOptions() {
      return this.allRouters.map(r => ({ value: r.id, label: r.name }))
    },
    cmdbOptions() {
      return this.cmdbDevices.map(d => ({ value: d.id, label: `${d.name} (${d.main_ip || '无IP'})` }))
    }
  },
  mounted() {
    this.loadList()
    this.loadAllRouters()
    this.loadCmdb()
    this.loadRegions()
  },
  methods: {
    regionName(regionId) {
      if (regionId === null || regionId === undefined) return null
      const g = this.regions.find(x => x.id === regionId)
      return g ? g.name : null
    },
    isV6Pool(p) {
      return p && (p.network || '').includes(':')
    },
    poolsOfRegion(regionId) {
      // '' = 全部；0 = 全局（跨地域网段）；注意不能用 !regionId 判断
      if (regionId === '' || regionId === null || regionId === undefined) return this.pools
      return this.pools.filter(p => p.region_id === regionId)
    },
    async loadRegions() {
      try {
        const res = await this.$axios.get('user/region/list')
        if (res.data.code === 200) {
          this.regions = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadCmdb() {
      try {
        const res = await this.$axios.get('user/cmdb/device')
        if (res.data.code === 200) {
          this.cmdbDevices = res.data.devices || []
        }
      } catch (e) { /* ignore */ }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/link-ip/list')
        if (res.data.code === 200) {
          this.pools = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },
    openPoolAddModal() {
      this.poolModal.network = ''
      this.poolModal.region_id = 0
      this.poolModal.remark = ''
      this.poolModal.show = true
    },
    openPoolEditModal(pool) {
      this.poolEditModal.pool = pool
      this.poolEditModal.region_id = (pool.region_id !== null && pool.region_id !== undefined) ? pool.region_id : 0
      this.poolEditModal.remark = pool.remark || ''
      this.poolEditModal.show = true
    },
    async openPushModal(a) {
      this.pushModal.item = a
      this.pushModal.a_interface = a.a_interface || '__none__'
      this.pushModal.b_interface = a.b_interface || '__none__'
      this.pushModal.aInterfaceOptions = []
      this.pushModal.bInterfaceOptions = []
      this.pushModal.show = true
      // 各自路由器的接口列表
      if (a.owner_a_type === 'router') this.loadInterfacesFor(a.router_a_id, 'aInterfaceOptions')
      if (a.owner_b_type === 'router') this.loadInterfacesFor(a.router_b_id, 'bInterfaceOptions')
    },
    async loadInterfacesFor(routerId, target) {
      try {
        const res = await this.$axios.get('user/interface/list', { params: { router_id: routerId } })
        if (res.data.code === 200) {
          const opts = (res.data.interfaces || []).map(i => ({ value: i.interface_name, label: i.interface_name }))
          opts.unshift({ value: '__none__', label: '（不下发）' })
          this.pushModal[target] = opts
        }
      } catch (e) { /* ignore */ }
    },
    async submitPush() {
      const a = this.pushModal.item
      if (!a) return
      const aIf = this.pushModal.a_interface === '__none__' ? '' : this.pushModal.a_interface
      const bIf = this.pushModal.b_interface === '__none__' ? '' : this.pushModal.b_interface
      if (!aIf && !bIf) {
        this.$toasted.show('至少选择一端网卡', { type: 'error' })
        return
      }
      this.pushModal.loading = true
      try {
        const res = await this.$axios.post(`user/link-ip/allocation/${a.id}/push`, {
          a_interface: aIf,
          b_interface: bIf
        })
        if (res.data.code === 200) {
          this.$toasted.show(res.data.msg || '已下发', { type: 'success' })
          this.pushModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '下发失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.pushModal.loading = false
    },
    async submitPoolEdit() {
      if (!this.poolEditModal.pool) return
      this.poolEditModal.loading = true
      try {
        const res = await this.$axios.post(`user/link-ip/pool/${this.poolEditModal.pool.id}/edit`, {
          region_id: this.poolEditModal.region_id,
          remark: this.poolEditModal.remark
        })
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.poolEditModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.poolEditModal.loading = false
    },
    async submitPool() {
      if (!this.poolModal.network) {
        this.$toasted.show('请填写网段', { type: 'error' })
        return
      }
      this.poolModal.loading = true
      try {
        const res = await this.$axios.post('user/link-ip/pool/add', {
          network: this.poolModal.network,
          region_id: this.poolModal.region_id,
          remark: this.poolModal.remark
        })
        if (res.data.code === 200) {
          this.$toasted.show('已创建', { type: 'success' })
          this.poolModal.show = false
          this.poolModal.network = ''
          this.poolModal.region_id = 0
          this.poolModal.remark = ''
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '创建失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.poolModal.loading = false
    },
    async openAllocModal(pool) {
      this.allocModal.pool = pool
      // pool 有地域时预选（注意 region_id=0 表示全局，不能用 ||）
      this.allocModal.region_id = (pool.region_id !== null && pool.region_id !== undefined) ? pool.region_id : ''
      this.allocModal.owner_a_type = 'router'
      this.allocModal.owner_a_id = ''
      this.allocModal.owner_b_type = 'router'
      this.allocModal.owner_b_id = ''
      this.allocModal.cidr = ''
      this.allocModal.remark = ''
      this.allocModal.freeList = []
      this.allocModal.show = true
      // 拉取空闲预览
      try {
        const res = await this.$axios.get(`user/link-ip/pool/${pool.id}/free`, { params: { limit: 10 } })
        if (res.data.code === 200) {
          this.allocModal.freeList = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    async submitAlloc() {
      const f = this.allocModal
      if (!f.owner_a_id || !f.owner_b_id) {
        this.$toasted.show('请选择 A/B 两端', { type: 'error' })
        return
      }
      if (f.owner_a_type === f.owner_b_type && f.owner_a_id === f.owner_b_id) {
        this.$toasted.show('A/B 不能相同', { type: 'error' })
        return
      }
      f.loading = true
      try {
        const res = await this.$axios.post('user/link-ip/allocate', {
          pool_id: f.pool.id,
          owner_a_type: f.owner_a_type,
          owner_a_id: f.owner_a_id,
          owner_b_type: f.owner_b_type,
          owner_b_id: f.owner_b_id,
          cidr: f.cidr,
          remark: f.remark
        })
        if (res.data.code === 200) {
          this.$toasted.show(`已分配 ${res.data.data.cidr}（${res.data.data.a_ip} / ${res.data.data.b_ip}）`, { type: 'success' })
          f.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '分配失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      f.loading = false
    },
    releaseAllocation(a, pool) {
      this.showConfirm('确认释放', `确认释放 ${a.cidr}（${a.owner_a_name} ${a.a_ip} / ${a.owner_b_name} ${a.b_ip}）？若 IP 已在 Tunnel/BGP 使用请先处理。`, async () => {
        try {
          const res = await this.$axios.delete(`user/link-ip/allocation/${a.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已释放', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '释放失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    deletePool(p) {
      this.showConfirm('确认删除', `确认删除网段池 ${p.network}？`, async () => {
        try {
          const res = await this.$axios.delete(`user/link-ip/pool/${p.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    }
  }
}
</script>

<style scoped>
.pool-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #e9ecef;
  border-radius: 4px;
  margin-bottom: 8px;
}
.ml-auto {
  margin-left: auto;
}
</style>
